import React, { ReactNode, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import classNames from 'classnames';
import { Routes } from 'routes';
import { selectIsCAOrganization } from 'common/features/store/duck/home/duck';
import { selectIsFlipgiveWalletBannerDisabledWeb } from 'common/features/store/duck/ffs';
import { IS_FLIPGIVE } from 'common/env';
import { createStyles } from 'utils/createStyle';
import AddCardModal from './AddCardModal';

export type WalletSection = 'archived' | 'active' | 'detail' | 'flipgive-gifts';

const styles = createStyles({
    main: [
        'md:grid mt-10 sm:mt-20 px-4',
        {
            gridTemplateColumns: 'auto max-content minmax(350px, 780px) auto',
        },
    ],
    menuSm: 'sm:hidden mb-2',
});

export const MainLayout = ({
    section,
    children,
}: {
    section: WalletSection;
    children?: ReactNode;
}) => {
    const archivedCardsRoute = `${Routes.WalletMyCards}?section=archived`;
    const myCardsRoute = `${Routes.WalletMyCards}?section=my-cards`;
    const flipgiveGiftsRoute = `${Routes.WalletMyCards}?section=flipgive-gifts`;

    const currentLinkStyles = 'no-underline text-brand-dark';

    const isMyCards = section === 'active';
    const isArchived = section === 'archived';
    const isFlipgiveGifts = section === 'flipgive-gifts';

    const [showModal, setShowModal] = useState(false);
    const isCAOrg = useSelector(selectIsCAOrganization);
    const isLegacyTabKillSwitchOn = useSelector(selectIsFlipgiveWalletBannerDisabledWeb);
    const showLegacyTab = IS_FLIPGIVE && isLegacyTabKillSwitchOn === false;

    const renderNavLinks = () => (
        <>
            <Link
                className={classNames('text-xs font-semibold', isMyCards && currentLinkStyles)}
                to={myCardsRoute}
            >
                My Gift Cards
            </Link>
            {showLegacyTab && (
                <Link
                    className={classNames(
                        'text-xs font-semibold',
                        isFlipgiveGifts && currentLinkStyles
                    )}
                    to={flipgiveGiftsRoute}
                >
                    Legacy Gift Cards
                </Link>
            )}
            <Link
                className={classNames('text-xs font-semibold', isArchived && currentLinkStyles)}
                to={archivedCardsRoute}
            >
                Archive
            </Link>
        </>
    );

    return (
        <div className={styles.main}>
            <div></div>
            <div>
                <div>
                    <div>
                        <AddCardModal isOpen={showModal} closeModal={() => setShowModal(false)} />
                        <h1 className="font-light text-brand-dark text-3xl sm:text-5xl mb-6 col-span-2">
                            Wallet
                        </h1>
                    </div>
                    <div className={styles.menuSm}>
                        <nav className="flex flex-row gap-8">{renderNavLinks()}</nav>
                    </div>
                    <div className="sm:hidden w-full border-t border-grey-5"></div>
                </div>
                <div className="hidden md:block">
                    <nav className="flex flex-col gap-4">{renderNavLinks()}</nav>
                </div>
            </div>
            <div>
                <div className="w-full items-center mr-auto flex">
                    {!isCAOrg && !isFlipgiveGifts ? (
                        <button
                            className={classNames(
                                'ml-auto mt-2 bg-white rounded-full text-brand py-2 px-7 w-max font-semibold -mt-32 md:mt-0 border-2 border-brand',
                                !isMyCards ? 'hidden' : 'visible'
                            )}
                            disabled={!isMyCards}
                            onClick={() => setShowModal(true)}
                        >
                            Add Card
                        </button>
                    ) : null}
                </div>
                <div className="max-w-3xl mt-12 w-full lg:ml-6">{children}</div>
            </div>
            <div></div>
        </div>
    );
};
